// Week 4 — CRUD mutations for the estimates slice.
// Optimistic cache update + Firestore persist. Snapshot undo before every write.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { saveAppState, saveSlice } from '../lib/db'
import { deleteShared } from '../lib/db/shared'
import { useUndoStore } from '../lib/undoStore'
import { useAssertCan } from './useCan'
import { today } from '../lib/fmt'
import { toastOk, toastErr } from '../lib/toastStore'
import type { AppState } from '../types/firestore'
import type { ActivityLogEntry, Estimate, EstimateCategory, EstimateItem, Project } from '../types'
import { makeActivityLogEntry } from '../lib/activityLog'
import { createPortalLink } from '../lib/portal'
import { ensurePortalLinkForProject, syncPortalWithProject } from '../lib/portalSync'

type ApprovalStatus = 'approved' | 'rejected' | 'pending' | 'draft'

export interface AddEstimateInput {
  name: string
  clientId?: number
  projId?: number
  date?: string
  expireDate?: string
  taxRate?: number
  terms?: string
  notes?: string
  noMargin?: boolean
  status?: Estimate['status']
  /** Pre-filled categories (from a template). IDs are renumbered on creation. */
  categories?: EstimateCategory[]
  // Investor / flip extras
  billingType?: 'fixed' | 'time_material'
  investorBudget?: number
  investorFeePct?: number
}

export interface EditItemPatch {
  desc?: string
  qty?: number
  cost?: number
  unit?: string | null
  /** number: set override. null: clear override. undefined: no change. */
  price?: number | null
}

export interface EditEstimateInput {
  name: string
  clientId?: number | null
  projId?: number | null
  date?: string
  expireDate?: string
  taxRate?: number
  terms?: string
  notes?: string
  noMargin?: boolean
  status?: Estimate['status']
}

export interface ConvertEstimateToProjectInput {
  estId: number
  name: string
  budget: number
  start: string
  status: Project['status']
  clientId?: number
  sqft?: number
  billingType?: Project['billingType']
}

export function useEstimateMutations() {
  const qc = useQueryClient()
  const { effectiveUid: uid, user } = useAuth()
  const pushUndo = useUndoStore((s) => s.push)
  const assertCan = useAssertCan()

  function getState(): AppState | undefined {
    return qc.getQueryData<AppState>(qk.appstate(uid!))
  }
  function snapshot() {
    const s = getState()
    if (s) pushUndo(s)
  }
  function refresh() {
    return qc.invalidateQueries({ queryKey: qk.appstate(uid!) })
  }

  function replaceEstimate(state: AppState, id: number, fn: (e: Estimate) => Estimate): Estimate[] {
    return state.estimates.map((e) => (e.id === id ? fn(e) : e))
  }

  // ── Estimate CRUD ─────────────────────────────────────────────────────────

  const add = useMutation({
    mutationFn: async (input: AddEstimateInput): Promise<number> => {
      assertCan('canCreateEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')
      const estId = state.nextEstId
      let nextEstItemId = state.nextEstItemId

      // Remap template category/item IDs to sequential real IDs
      const categories: EstimateCategory[] = (input.categories ?? []).map((c) => ({
        ...c,
        id: nextEstItemId++,
        items: c.items.map((it) => ({ ...it, id: nextEstItemId++ })),
      }))

      const estimate: Estimate = {
        id: estId,
        name: input.name,
        status: input.status ?? 'draft',
        categories,
        ...(input.clientId ? { clientId: input.clientId } : {}),
        ...(input.projId ? { projId: input.projId } : {}),
        ...(input.date ? { date: input.date } : {}),
        ...(input.expireDate ? { expireDate: input.expireDate } : {}),
        ...(input.taxRate != null ? { taxRate: input.taxRate } : {}),
        ...(input.terms ? { terms: input.terms } : {}),
        ...(input.notes ? { notes: input.notes } : {}),
        ...(input.noMargin ? { noMargin: true } : {}),
        ...(input.billingType ? { billingType: input.billingType } : {}),
        ...(input.investorBudget != null ? { investorBudget: input.investorBudget } : {}),
        ...(input.investorFeePct != null ? { investorFeePct: input.investorFeePct } : {}),
      }

      const next: AppState = {
        ...state,
        estimates: [...state.estimates, estimate],
        nextEstId: state.nextEstId + 1,
        nextEstItemId,
      }
      qc.setQueryData(qk.appstate(uid), next)
      await saveAppState(uid, next)
      // Auto-generate a portal link at creation — the same token rides with
      // the estimate through edit + convert-to-project. Client just keeps
      // getting updated info at the same URL. Best-effort; never blocks.
      void (async () => {
        try {
          const client = estimate.clientId != null
            ? next.clients.find((c) => c.id === estimate.clientId)
            : undefined
          const linkedClientUid = client?.linkedUid
          const { token } = await createPortalLink({
            ownerUid: uid, estimate, client,
            ...(linkedClientUid !== undefined ? { linkedClientUid } : {}),
          })
          const curr = getState()
          if (!curr) return
          const patched = curr.estimates.map((e): Estimate => {
            if (e.id !== estId) return e
            const existing = e.portalTokens ?? []
            if (existing.includes(token)) return e
            return { ...e, portalTokens: [...existing, token] }
          })
          qc.setQueryData(qk.appstate(uid), { ...curr, estimates: patched })
          await saveSlice(uid, 'estimates', patched)
        } catch (err) {
          console.warn('[estimate-add] auto-portal-link failed', err)
        }
      })()
      return estId
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  const edit = useMutation({
    mutationFn: async ({ id, input }: { id: number; input: EditEstimateInput }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const newEstimates = replaceEstimate(state, id, (e) => {
        const u: Estimate = { ...e, name: input.name || e.name }

        if (input.clientId === null)      delete u.clientId
        else if (input.clientId != null)  u.clientId = input.clientId

        if (input.projId === null)        delete u.projId
        else if (input.projId != null)    u.projId = input.projId

        if (input.date != null)           u.date = input.date
        if (input.expireDate != null)     u.expireDate = input.expireDate
        if (input.taxRate != null)        u.taxRate = input.taxRate
        if (input.terms != null)          u.terms = input.terms
        if (input.notes != null)          u.notes = input.notes
        if (input.status != null)         u.status = input.status
        if (input.noMargin)               u.noMargin = true
        else                              delete u.noMargin

        return u
      })

      const nextState: AppState = { ...state, estimates: newEstimates }
      qc.setQueryData(qk.appstate(uid), nextState)
      await saveSlice(uid, 'estimates', newEstimates)
      // Portal sync — if the estimate is linked to a project, push a fresh
      // snapshot so the web portal reflects the updated fields (noMargin,
      // taxRate, name, etc.). Mirrors useChangeOrderMutations pattern.
      const editedEst = newEstimates.find((e) => e.id === id)
      if (editedEst?.projId != null) {
        void syncPortalWithProject(uid, editedEst.projId, nextState).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (edit)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      // ─── Cascade shared docs (V1 L12904-12933 parity) ───────────────────
      // 1. estshare__<_fromLink> — delete the original shared-estimate doc
      //    so the receiver's heal poll drops their local copy.
      //    Only if WE own the estimate (we sent it); don't cascade when
      //    we're the receiver of someone else's share.
      // 2. portallink__<token> — delete every portal link attached.
      //    Also guarded — receiver's copy of a shared estimate never owns
      //    portal tokens (sender's scope), but defensively skipped for
      //    incoming estimates in case a legacy record carries stale tokens.
      // Note: we do NOT cascade-delete the linked project even if one
      // exists — deleting the estimate only detaches it; the project
      // has its own delete path with its own cascade.
      const est = state.estimates.find((e) => e.id === id)
      if (est) {
        const rawEst = est as unknown as {
          _fromLink?: string; _incomingLocked?: boolean; _fromUid?: string
        }
        // Sender-side: we own the estshare doc. Receiver-side (_fromUid !== uid,
        // _incomingLocked) leaves the shared doc alone — the sender still owns
        // it. Local delete still proceeds (in-memory filter below).
        const isIncoming = !!rawEst._incomingLocked
          || (!!rawEst._fromUid && rawEst._fromUid !== uid)
        if (!isIncoming && typeof rawEst._fromLink === 'string' && rawEst._fromLink) {
          // Fire-and-forget; don't block local delete.
          void deleteShared(rawEst._fromLink).catch((err) => {
            console.warn('[estimate-delete] estshare delete failed', rawEst._fromLink, err)
          })
        }
        if (!isIncoming && est.portalTokens && est.portalTokens.length) {
          for (const tok of est.portalTokens) {
            void deleteShared('portallink__' + tok).catch((err) => {
              console.warn('[estimate-delete] portallink delete failed', tok, err)
            })
          }
        }
      }

      const newEstimates = state.estimates.filter((e) => e.id !== id)
      qc.setQueryData(qk.appstate(uid), { ...state, estimates: newEstimates })
      await saveSlice(uid, 'estimates', newEstimates)
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  // V1→V2 parity: push a freshly minted portal token onto
  // estimate.portalTokens so subsequent syncPortalWithProject calls can find
  // it. createPortalLink writes the shared doc but (until now) never wrote
  // the token back to the estimate row, breaking portal sync.
  const addPortalToken = useMutation({
    mutationFn: async ({ estId, token }: { estId: number; token: string }) => {
      // No permission assert — createPortalLink was already gated by the
      // caller's canEditEstimate / "send to client" button.
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')
      const newEstimates = state.estimates.map((e): Estimate => {
        if (e.id !== estId) return e
        const existing = e.portalTokens ?? []
        if (existing.includes(token)) return e
        return { ...e, portalTokens: [...existing, token] }
      })
      qc.setQueryData(qk.appstate(uid), { ...state, estimates: newEstimates })
      await saveSlice(uid, 'estimates', newEstimates)
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  // V1 parity: status transitions carry approval-workflow side effects.
  //   → 'sent'     : mark approval 'pending' + stamp approvalSentAt
  //                  (V1 markEstimatePending, public/app/index.html:13358-13360)
  //   → 'accepted' : mirror clientPortalStatus='accepted'; if approvalStatus
  //                  was 'pending', auto-promote to 'approved'
  //                  (V1 public/app/index.html:13291-13294, :21296)
  //   → 'declined' : mirror clientPortalStatus='declined'
  //                  (V1 public/app/index.html:13299-13300, :21299)
  //
  // source='portal' — invoked from usePortalResponses when the client accepts
  // or declines via the portal. Stamps approvedAt/approvedBy='Client via portal'
  // on auto-promote and appends an activity log entry. Manual investor flips
  // (source='manual' or omitted) continue to leave the stamp fields alone —
  // those are set by setApprovalStatus when the investor signs off internally.
  const changeStatus = useMutation({
    mutationFn: async ({ id, status, source }: {
      id: number
      status: NonNullable<Estimate['status']>
      source?: 'portal' | 'manual'
    }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')
      const nowIso = new Date().toISOString()
      let autoPromoted = false
      let portalAccepted = false
      let portalDeclined = false
      const existing = state.estimates.find((e) => e.id === id)
      // DIAG: log items before/after so we can see if this path is zeroing.
      if (existing) {
        const dump = existing.categories?.flatMap((c) => (c.items ?? []).map((it) => {
          const amt = (it as unknown as { amount?: number }).amount
          return { cat: c.name, desc: it.desc, cost: it.cost, qty: it.qty, amount: amt }
        }))
        console.log('[ZEROOUT-DIAG] changeStatus BEFORE est-' + id + ' status=' + status + ' source=' + source, dump)
      }
      const newEstimates = replaceEstimate(state, id, (e) => {
        const u: Estimate = { ...e, status }
        if (status === 'sent') {
          u.approvalStatus = 'pending'
          u.approvalSentAt = nowIso
        } else if (status === 'accepted') {
          if (e.clientPortalStatus !== 'accepted') u.clientPortalStatus = 'accepted'
          if ((e.approvalStatus ?? 'draft') === 'pending') {
            u.approvalStatus = 'approved'
            autoPromoted = true
            if (source === 'portal') {
              u.approvedAt = nowIso
              u.approvedBy = 'Client via portal'
            }
          }
          if (source === 'portal') portalAccepted = true
        } else if (status === 'declined') {
          if (e.clientPortalStatus !== 'declined') u.clientPortalStatus = 'declined'
          if (source === 'portal') portalDeclined = true
        }
        return u
      })

      const nextState: AppState = { ...state, estimates: newEstimates }

      // V1 parity: log a 'Client accepted' / 'Client declined' activity entry
      // when the transition originated from the portal listener, so the Activity
      // feed shows who moved the state.
      if (portalAccepted || portalDeclined) {
        const entry = makeActivityLogEntry(
          'portal',
          portalAccepted ? 'estimate.portal_accepted' : 'estimate.portal_declined',
          id,
          portalAccepted ? 'Client accepted estimate via portal' : 'Client declined estimate via portal',
          {
            estName: existing?.name ?? '',
            ...(portalAccepted && autoPromoted ? { approvalAutoPromoted: true } : {}),
          },
        )
        nextState.activityLog = [...state.activityLog, entry]
      }

      qc.setQueryData(qk.appstate(uid), nextState)
      if (nextState.activityLog !== state.activityLog) {
        await saveAppState(uid, nextState)
      } else {
        await saveSlice(uid, 'estimates', newEstimates)
      }
      // DIAG: log items AFTER save completes.
      const afterSave = nextState.estimates.find((e) => e.id === id)
      if (afterSave) {
        const dump = afterSave.categories?.flatMap((c) => (c.items ?? []).map((it) => {
          const amt = (it as unknown as { amount?: number }).amount
          return { cat: c.name, desc: it.desc, cost: it.cost, qty: it.qty, amount: amt }
        }))
        console.log('[ZEROOUT-DIAG] changeStatus AFTER est-' + id, dump)
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  // Internal approve/reject (v1 parity: approveEstimateStatus / rejectEstimateStatus).
  // Separate from the outward `status` field and the client-portal flow — this
  // is the internal sign-off on whether the estimate is green-lit to send /
  // turn into a project.
  const setApprovalStatus = useMutation({
    mutationFn: async ({ id, status, reason }: { id: number; status: ApprovalStatus; reason?: string }) => {
      assertCan('canApproveEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const nowIso = new Date().toISOString()
      const est = state.estimates.find((e) => e.id === id)
      if (!est) throw new Error(`Estimate ${id} not found`)

      const newEstimates = replaceEstimate(state, id, (e) => {
        const u: Estimate = { ...e, approvalStatus: status }
        if (status === 'approved') {
          u.approvedAt = nowIso
          u.approvedBy = user?.displayName || user?.email || 'Unknown'
          delete u.rejectedAt
          delete u.rejectedReason
        } else if (status === 'rejected') {
          u.rejectedAt = nowIso
          u.rejectedReason = reason || ''
          delete u.approvedAt
          delete u.approvedBy
        }
        return u
      })

      const entry: ActivityLogEntry = makeActivityLogEntry(
        uid,
        'estimate',
        id,
        status === 'approved' ? 'Approved estimate' : 'Rejected estimate',
        {
          estName: est.name,
          ...(status === 'rejected' && reason ? { reason } : {}),
        },
      )

      const next: AppState = {
        ...state,
        estimates: newEstimates,
        activityLog: [...state.activityLog, entry],
      }
      qc.setQueryData(qk.appstate(uid), next)
      await saveAppState(uid, next)
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  // ── Convert estimate → project (v1 parity: "Approve & create project") ────
  // Creates a new project from the estimate, links estimate.projId, flips
  // estimate.status to 'accepted', and appends an activity log entry.
  const convertToProject = useMutation({
    mutationFn: async (input: ConvertEstimateToProjectInput): Promise<number> => {
      assertCan('canCreateProject')
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const est = state.estimates.find((e) => e.id === input.estId)
      if (!est) throw new Error(`Estimate ${input.estId} not found`)
      if (est.projId) throw new Error('Estimate already linked to a project')

      const projId = state.nextProjId
      // V1 parity (public/app/index.html:13449): for an incoming shared
      // estimate (_incomingLocked), carry the sender provenance onto the new
      // project so the client-side sees the correct partner attribution and
      // the "received from" badge.
      //
      // STRICT (Bug 1 redux, 2026-04-22): only `_incomingLocked === true`
      // counts as evidence of incoming-ness. The old widened check also
      // accepted any `_fromLink` / `_fromDealLink` / `_fromUid` marker, but
      // those fields are ALSO copied onto sender-side estimates in some
      // outgoing-share flows (for undo/resurrect — see receivedHeal.ts
      // comment). Trusting them here stamped `_received: true` on a GC's
      // own converted estimate, which cascaded into every downstream
      // received-project UI. `_incomingLocked` is set by exactly one path
      // (handleAcceptIncoming in routes/Estimates.tsx — the estshare
      // acceptance) and is the only reliable signal.
      const rawEst = est as unknown as {
        _incomingLocked?: boolean
        _fromLink?: string
        _fromDealLink?: string
        _fromUid?: string
        _fromName?: string
      }
      const isIncoming = rawEst._incomingLocked === true
      const project: Project = {
        id: projId,
        name: input.name,
        budget: input.budget,
        status: input.status,
        start: input.start || today(),
        ...(input.clientId ? { clientId: input.clientId } : {}),
        ...(input.sqft ? { sqft: input.sqft } : {}),
        ...(input.billingType === 'time_material' ? { billingType: 'time_material' } : {}),
        ...(isIncoming ? {
          // Mirror V1 (public/app/index.html:13449) + robust parity: stamp ALL
          // provenance markers so the received-detail render path has every
          // signal it needs — no external lookups required. `_incomingLocked`
          // is mirrored from the estimate so the project carries the same
          // authoritative marker.
          _received: true,
          _incomingLocked: true,
          ...(rawEst._fromName ? { _fromName: rawEst._fromName } : {}),
          ...(rawEst._fromUid  ? { _fromUid:  rawEst._fromUid  } : {}),
          // _fromLink: deallink id preferred (used by ReceivedProjectPortal to
          // match coshare/msgshare/paymshare docs). Falls back to estshare id
          // for older estimates; ReceivedProjectPortal self-heals that case.
          ...((rawEst._fromDealLink || rawEst._fromLink) ? { _fromLink: rawEst._fromDealLink || rawEst._fromLink || '' } : {}),
          // _fromDealLink: keep the deallink id separately for robustness.
          ...(rawEst._fromDealLink ? { _fromDealLink: rawEst._fromDealLink } : {}),
        } : {}),
      }

      const newEstimates = replaceEstimate(state, input.estId, (e) => ({
        ...e,
        projId,
        status: 'accepted',
      }))

      const entry: ActivityLogEntry = makeActivityLogEntry(
        uid,
        'estimate.convert_to_project',
        projId,
        `Converted estimate "${est.name}" to project "${input.name}"`,
        {
          estId: input.estId,
          estName: est.name,
          projectName: input.name,
          budget: input.budget,
        },
      )

      const next: AppState = {
        ...state,
        projects:    [...state.projects, project],
        estimates:   newEstimates,
        activityLog: [...state.activityLog, entry],
        nextProjId:  state.nextProjId + 1,
      }

      qc.setQueryData(qk.appstate(uid), next)
      await saveAppState(uid, next)
      // Auto-create the portallink__ doc for the new project (V1 parity).
      // Skipped on incoming-locked estimates — the sender owns the portal
      // for their side; the receiver shouldn't mint a new one.
      if (!isIncoming) {
        const linkedClientForPortal = input.clientId != null
          ? next.clients.find((c) => c.id === input.clientId)
          : undefined
        void ensurePortalLinkForProject(
          uid, projId, next, linkedClientForPortal?.linkedUid,
        ).catch((err) => console.warn(err))
      }
      return projId
    },
    onError: (e) => {
      toastErr(`Convert failed: ${e instanceof Error ? e.message : String(e)}`)
      void refresh()
    },
    onSuccess: () => {
      toastOk('Estimate converted to project')
      void refresh()
    },
  })

  // ── Category CRUD ─────────────────────────────────────────────────────────

  const addCategory = useMutation({
    mutationFn: async ({ estId, name }: { estId: number; name: string }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const nextItemId = state.nextEstItemId
      const newEstimates = replaceEstimate(state, estId, (e) => {
        const cat: EstimateCategory = { id: nextItemId, name, items: [] }
        return { ...e, categories: [...(e.categories ?? []), cat] }
      })
      const next: AppState = {
        ...state,
        estimates: newEstimates,
        nextEstItemId: state.nextEstItemId + 1,
      }
      qc.setQueryData(qk.appstate(uid), next)
      await saveAppState(uid, next)
      // Portal sync — empty category doesn't change total yet, but wiring it
      // here is consistent and cheap (sync is a no-op when projId absent).
      const addedEst = next.estimates.find((e) => e.id === estId)
      if (addedEst?.projId != null) {
        void syncPortalWithProject(uid, addedEst.projId, next).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (addCategory)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  const renameCategory = useMutation({
    mutationFn: async ({ estId, catId, name }: { estId: number; catId: number; name: string }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')
      const newEstimates = replaceEstimate(state, estId, (e) => ({
        ...e,
        categories: (e.categories ?? []).map((c) => (c.id === catId ? { ...c, name } : c)),
      }))
      qc.setQueryData(qk.appstate(uid), { ...state, estimates: newEstimates })
      await saveSlice(uid, 'estimates', newEstimates)
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  // Apply a template's categories + items to an existing estimate by appending
  // (new IDs allocated from nextEstItemId). Source category/item IDs are
  // ignored — this only cares about desc/qty/cost/unit.
  const applyTemplate = useMutation({
    mutationFn: async (
      { estId, categories }: {
        estId: number
        categories: { name: string; items: { desc: string; qty: number; cost: number; unit?: string }[] }[]
      },
    ) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      let nextItemId = state.nextEstItemId
      const mapped: EstimateCategory[] = categories.map((c) => {
        const catId = nextItemId++
        return {
          id: catId,
          name: c.name,
          items: c.items.map((it) => ({
            id: nextItemId++,
            desc: it.desc,
            qty: it.qty,
            cost: it.cost,
            ...(it.unit ? { unit: it.unit } : {}),
          })),
        }
      })

      const newEstimates = replaceEstimate(state, estId, (e) => ({
        ...e,
        categories: [...(e.categories ?? []), ...mapped],
      }))

      const next: AppState = {
        ...state,
        estimates: newEstimates,
        nextEstItemId: nextItemId,
      }
      qc.setQueryData(qk.appstate(uid), next)
      await saveAppState(uid, next)
      // Portal sync — template application adds items that change the total.
      const tplEst = next.estimates.find((e) => e.id === estId)
      if (tplEst?.projId != null) {
        void syncPortalWithProject(uid, tplEst.projId, next).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (applyTemplate)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  const removeCategory = useMutation({
    mutationFn: async ({ estId, catId }: { estId: number; catId: number }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')
      const newEstimates = replaceEstimate(state, estId, (e) => ({
        ...e,
        categories: (e.categories ?? []).filter((c) => c.id !== catId),
      }))
      const nextState: AppState = { ...state, estimates: newEstimates }
      qc.setQueryData(qk.appstate(uid), nextState)
      await saveSlice(uid, 'estimates', newEstimates)
      // Portal sync — removing a category changes the total.
      const removedFromEst = nextState.estimates.find((e) => e.id === estId)
      if (removedFromEst?.projId != null) {
        void syncPortalWithProject(uid, removedFromEst.projId, nextState).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (removeCategory)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  // ── Item CRUD ─────────────────────────────────────────────────────────────

  const addItem = useMutation({
    mutationFn: async ({ estId, catId, item }: { estId: number; catId: number; item: Omit<EstimateItem, 'id'> }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const nextItemId = state.nextEstItemId
      const newEstimates = replaceEstimate(state, estId, (e) => ({
        ...e,
        categories: (e.categories ?? []).map((c) => {
          if (c.id !== catId) return c
          const newItem: EstimateItem = {
            id: nextItemId,
            desc: item.desc,
            qty: item.qty,
            cost: item.cost,
            ...(item.unit ? { unit: item.unit } : {}),
            ...(item.price != null ? { price: item.price } : {}),
          }
          return { ...c, items: [...c.items, newItem] }
        }),
      }))
      const next: AppState = {
        ...state,
        estimates: newEstimates,
        nextEstItemId: state.nextEstItemId + 1,
      }
      qc.setQueryData(qk.appstate(uid), next)
      await saveAppState(uid, next)
      // Portal sync — adding an item changes the total.
      const addItemEst = next.estimates.find((e) => e.id === estId)
      if (addItemEst?.projId != null) {
        void syncPortalWithProject(uid, addItemEst.projId, next).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (addItem)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  const editItem = useMutation({
    mutationFn: async (
      { estId, catId, itemId, patch }:
      { estId: number; catId: number; itemId: number; patch: EditItemPatch },
    ) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const newEstimates = replaceEstimate(state, estId, (e) => ({
        ...e,
        categories: (e.categories ?? []).map((c) => {
          if (c.id !== catId) return c
          return {
            ...c,
            items: c.items.map((it): EstimateItem => {
              if (it.id !== itemId) return it
              const u: EstimateItem = { ...it }
              if (patch.desc != null)  u.desc = patch.desc
              if (patch.qty  != null)  u.qty  = patch.qty
              if (patch.cost != null)  u.cost = patch.cost
              if (patch.unit !== undefined) {
                if (patch.unit === null || patch.unit === '') delete u.unit
                else u.unit = patch.unit
              }
              if (patch.price !== undefined) {
                if (patch.price === null) delete u.price
                else                      u.price = patch.price
              }
              return u
            }),
          }
        }),
      }))
      const nextState: AppState = { ...state, estimates: newEstimates }
      qc.setQueryData(qk.appstate(uid), nextState)
      await saveSlice(uid, 'estimates', newEstimates)
      // Portal sync — editing an item changes qty/cost/price, altering the total.
      const editItemEst = nextState.estimates.find((e) => e.id === estId)
      if (editItemEst?.projId != null) {
        void syncPortalWithProject(uid, editItemEst.projId, nextState).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (editItem)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  const removeItem = useMutation({
    mutationFn: async ({ estId, catId, itemId }: { estId: number; catId: number; itemId: number }) => {
      assertCan('canEditEstimate')
      snapshot()
      const state = getState()
      if (!state || !uid) throw new Error('State not loaded')

      const newEstimates = replaceEstimate(state, estId, (e) => ({
        ...e,
        categories: (e.categories ?? []).map((c) => {
          if (c.id !== catId) return c
          return { ...c, items: c.items.filter((it) => it.id !== itemId) }
        }),
      }))
      const nextState: AppState = { ...state, estimates: newEstimates }
      qc.setQueryData(qk.appstate(uid), nextState)
      await saveSlice(uid, 'estimates', newEstimates)
      // Portal sync — removing an item lowers the total.
      const removeItemEst = nextState.estimates.find((e) => e.id === estId)
      if (removeItemEst?.projId != null) {
        void syncPortalWithProject(uid, removeItemEst.projId, nextState).catch((err) =>
          console.warn('[estimateMutations] portal sync failed (removeItem)', err))
      }
    },
    onError: () => void refresh(),
    onSuccess: () => void refresh(),
  })

  return {
    add, edit, remove, changeStatus, setApprovalStatus, convertToProject, applyTemplate,
    addCategory, renameCategory, removeCategory,
    addItem, editItem, removeItem, addPortalToken,
  }
}
